// Diagram component - handles SVG interaction
import { appState } from "../utils/appState";
import { analytics } from "../utils/analytics";
import type { SegmentType, SegmentKey } from "../types/segment";

export class Diagram {
  private container: HTMLElement;
  private svg: SVGSVGElement | null = null;
  private activeSegmentEl: SVGGElement | null = null;

  constructor(containerSelector: string) {
    const element = document.querySelector(containerSelector);
    if (!element) throw new Error(`Element "${containerSelector}" not found`);

    this.container = element as HTMLElement;
  }

  async init(): Promise<void> {
    await this.loadSVG();
    this.setupEventListeners();
    this.setupKeyboardNavigation();

    // Listen to locale changes
    window.addEventListener("localechange", async () => {
      await this.loadSVG();
      this.setupEventListeners();
    });

    // Subscribe to state changes
    appState.activeSegment.subscribe((segment) => {
      if (!segment && this.activeSegmentEl) {
        const segmentToDeactivate = this.activeSegmentEl;

        // Return focus to segment if opened via keyboard
        if (appState.openedViaKeyboard.get()) {
          segmentToDeactivate.focus();
          // Delay deactivation slightly to show focus state
          setTimeout(() => {
            this.deactivateSegment(segmentToDeactivate);
          }, 50);
        } else {
          this.deactivateSegment(segmentToDeactivate);
        }
      }
    });
  }

  private async loadSVG(): Promise<void> {
    const locale = appState.locale.get();

    try {
      const response = await fetch(`/assets/diagram-${locale}.svg`);

      if (!response.ok) {
        throw new Error(
          `Failed to load SVG: ${response.status} ${response.statusText}`
        );
      }

      const svgText = await response.text();

      // Find the SVG wrapper and insert inline SVG
      const svgWrapper = this.container.querySelector(".svg-wrapper");
      if (svgWrapper) {
        // Preserve title-overlay if it exists
        const titleOverlay = svgWrapper.querySelector(".title-overlay");

        // Insert SVG
        svgWrapper.innerHTML = svgText;

        // Re-append title-overlay after SVG
        if (titleOverlay) {
          svgWrapper.appendChild(titleOverlay);
        }

        this.svg = svgWrapper.querySelector("svg");
      } else {
        console.error(".svg-wrapper not found in container");
      }
    } catch (error) {
      console.error("Error loading SVG:", error);
      throw error;
    }
  }

  private setupEventListeners(): void {
    if (!this.svg) return;

    const segments = this.svg.querySelectorAll("[data-segment-key]");

    segments.forEach((segment) => {
      const el = segment as SVGGElement;

      el.addEventListener("click", () => this.handleSegmentClick(el));

      // Hover effects (desktop only)
      if (window.innerWidth >= 768) {
        el.addEventListener("mouseenter", () => this.handleSegmentHover(el));
        el.addEventListener("mouseleave", () => this.handleSegmentLeave(el));
      }
    });
  }

  private handleSegmentClick(segment: SVGGElement, viaKeyboard = false): void {
    const segmentType = segment.dataset.segmentType as SegmentType;
    const segmentKey = segment.dataset.segmentKey as SegmentKey;

    if (!segmentType || !segmentKey) return;

    // Track analytics
    analytics.trackSegmentClick(segmentType, segmentKey);

    // Toggle if clicking same segment
    if (this.activeSegmentEl === segment) {
      this.deactivateSegment(segment);
      appState.activeSegment.set(null);
      appState.isPanelOpen.set(false);
      return;
    }

    // Deactivate previous
    if (this.activeSegmentEl) {
      this.deactivateSegment(this.activeSegmentEl);
    }

    // Activate new
    this.activateSegment(segment);
    appState.activeSegment.set({ type: segmentType, key: segmentKey });
    appState.isPanelOpen.set(true);
    appState.openedViaKeyboard.set(viaKeyboard);
  }

  private activateSegment(segment: SVGGElement): void {
    this.activeSegmentEl = segment;

    segment.classList.add("active");
    segment.setAttribute("aria-expanded", "true");

    // Apply radial expansion
    const expansion = this.calculateExpansion(segment);
    segment.style.transform = `translate(${expansion.x}px, ${expansion.y}px)`;

    // Ensure focus is maintained
    segment.focus();

    // Dim others
    this.svg
      ?.querySelectorAll("[data-segment-key]:not(.active)")
      .forEach((el) => {
        (el as SVGGElement).style.opacity = "0.5";
      });
  }

  private deactivateSegment(segment: SVGGElement): void {
    segment.classList.remove("active");
    segment.setAttribute("aria-expanded", "false");
    segment.style.transform = "";

    // Restore opacity
    this.svg?.querySelectorAll("[data-segment-key]").forEach((el) => {
      (el as SVGGElement).style.opacity = "1";
    });

    this.activeSegmentEl = null;
  }

  private calculateExpansion(segment: SVGGElement): { x: number; y: number } {
    const centerX = 175;
    const centerY = 175;
    const expansionDistance = 3;

    const bbox = segment.getBBox();
    const centroidX = bbox.x + bbox.width / 2;
    const centroidY = bbox.y + bbox.height / 2;

    const angle = Math.atan2(centroidY - centerY, centroidX - centerX);

    return {
      x: Math.cos(angle) * expansionDistance,
      y: Math.sin(angle) * expansionDistance,
    };
  }

  private handleSegmentHover(segment: SVGGElement): void {
    if (this.activeSegmentEl) return;

    // Apply radial expansion
    const expansion = this.calculateExpansion(segment);
    segment.style.transform = `translate(${expansion.x}px, ${expansion.y}px)`;
    segment.style.transition = "transform var(--transition-bounce)";
  }

  private handleSegmentLeave(segment: SVGGElement): void {
    if (this.activeSegmentEl === segment) return;

    // Remove expansion
    segment.style.transform = "";
  }

  private setupKeyboardNavigation(): void {
    if (!this.svg) return;

    const segments = Array.from(
      this.svg.querySelectorAll("[data-segment-key]")
    ) as SVGGElement[];

    segments.forEach((segment, index) => {
      segment.addEventListener("keydown", (e) => {
        switch (e.key) {
          case "Enter":
          case " ":
            e.preventDefault();
            this.handleSegmentClick(segment, true);
            break;

          case "ArrowRight":
          case "ArrowDown":
            e.preventDefault();
            segments[(index + 1) % segments.length]?.focus();
            break;

          case "ArrowLeft":
          case "ArrowUp":
            e.preventDefault();
            segments[(index - 1 + segments.length) % segments.length]?.focus();
            break;

          case "Escape":
            e.preventDefault();
            if (this.activeSegmentEl) {
              this.handleSegmentClick(this.activeSegmentEl);
            }
            break;
        }
      });
    });
  }

  public closeActiveSegment(): void {
    if (this.activeSegmentEl) {
      this.deactivateSegment(this.activeSegmentEl);
      appState.activeSegment.set(null);
      appState.isPanelOpen.set(false);
    }
  }
}
